import { useCallback, useEffect, useRef, useState } from "react";
import {
  hasAdminView,
  useAdminViewRestoring,
  useAdminViewState,
} from "../../lib/admin/adminViewState";
import { Link } from "react-router-dom";
import { apiFetch } from "../../api/client";

type Occupant = {
  peerId: string;
  label: string;
  userId: string | null;
  canEdit: boolean;
  guest: boolean;
};

type OnlineBoard = {
  board_id: string;
  title: string | null;
  owner_email: string | null;
  peers: number;
  silent: number;
  occupants: Occupant[];
};

const POLL_MS = 5000;

export function AdminOnlineTab({ kind }: { kind: "school" | "external" }) {
  const viewKey = `admin:${kind}:online`;
  const restored = useAdminViewRestoring() && hasAdminView(viewKey);
  const [boards, setBoards] = useAdminViewState<OnlineBoard[]>(viewKey, []);
  const [err, setErr] = useState("");
  const [firstLoad, setFirstLoad] = useState(!restored);
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await apiFetch<{ boards: OnlineBoard[] }>(
        `/api/v1/admin/online?kind=${kind}`
      );
      if (!aliveRef.current) return;
      setBoards(data.boards ?? []);
      setErr("");
    } catch (e) {
      if (!aliveRef.current) return;
      setErr(e instanceof Error ? e.message : "Не удалось получить список");
    } finally {
      if (aliveRef.current) setFirstLoad(false);
    }
  }, [kind]);

  useEffect(() => {
    aliveRef.current = true;
    void load();
    const t = window.setInterval(() => void load(), POLL_MS);
    return () => {
      aliveRef.current = false;
      window.clearInterval(t);
    };
  }, [load]);

  const totalPeers = boards.reduce((n, b) => n + b.peers, 0);

  return (
    <div className="wb-admin-users">
      <div className="wb-admin-users__head">
        <h2 className="wb-admin-users__title">
          Сейчас на досках{" "}
          <span className="wb-admin-users__count">
            {totalPeers} чел. на {boards.length} досках
          </span>
        </h2>
      </div>
      <p className="wb-cabinet-plan-hint__text">
        Показаны доски этого контура и гостевые (их не отнести ни к школьным, ни к
        внешним). Обновляется каждые {POLL_MS / 1000} секунд. Присутствие хранится в памяти процесса
        API, поэтому при нескольких инстансах за балансировщиком список покажет только тот,
        который ответил на запрос.
      </p>
      {err ? (
        <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
          {err}
        </p>
      ) : null}

      <div className="wb-admin-table-wrap">
        <table className="wb-admin-table">
          <thead>
            <tr>
              <th>Доска</th>
              <th>Владелец</th>
              <th>Человек</th>
              <th>Кто</th>
            </tr>
          </thead>
          <tbody>
            {boards.map((b) => (
              <tr key={b.board_id}>
                <td>
                  <Link
                    to={`/board/${b.board_id}?spectate=1`}
                    title="Невидимый вход: только смотреть, участники не видят"
                  >
                    {b.title || "Без названия"}
                  </Link>
                </td>
                <td>{b.owner_email ?? "гостевая"}</td>
                <td>
                  {b.peers}
                  {b.silent > 0 ? ` (+${b.silent} невидимо)` : ""}
                </td>
                <td>
                  {b.occupants
                    .map((o) => `${o.label}${o.canEdit ? "" : " (смотрит)"}`)
                    .join(", ") || "—"}
                </td>
              </tr>
            ))}
            {!boards.length && !firstLoad ? (
              <tr>
                <td colSpan={4}>Сейчас никого нет</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
